(() => {
  'use strict';

  const mediaCleanupMap = new WeakMap();

  const registerMediaCleanup = (root, cleanupFn) => {
    if (!root || typeof cleanupFn !== 'function') return;
    const cleanups = mediaCleanupMap.get(root) || [];
    cleanups.push(cleanupFn);
    mediaCleanupMap.set(root, cleanups);
  };

  const runMediaCleanup = (root) => {
    const cleanups = mediaCleanupMap.get(root);
    if (!cleanups || !cleanups.length) return;

    cleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.warn('PDP media cleanup error', error);
      }
    });

    mediaCleanupMap.delete(root);
  };

  const forEachMediaRootInScope = (scope, callback) => {
    if (!scope || typeof callback !== 'function') return;

    if (scope.matches && scope.matches('[data-custom-pdp-media]')) {
      callback(scope);
    }

    scope.querySelectorAll('[data-custom-pdp-media]').forEach((root) => callback(root));
  };

  const initCustomPdpMedia = (rootNode = document) => {
    rootNode.querySelectorAll('[data-custom-pdp-media]').forEach((root) => {
      if (root.dataset.mediaInitialized === 'true') return;
      root.dataset.mediaInitialized = 'true';

      const mainImages = Array.from(root.querySelectorAll('[data-main-media-id]'));
      const thumbs = Array.from(root.querySelectorAll('[data-target-media-id]'));
      const thumbSwiperEl = root.querySelector('[data-pdp-thumb-swiper]');
      const mainContainer = root.querySelector('.custom-pdp-media__main');
      const valueBadge = root.querySelector('.custom-pdp-media__value-badge');
      const tagsContainer = root.querySelector('.custom-pdp-media__tags');
      const variantFeaturedMediaIds = new Set(
        (root.dataset.variantFeaturedMediaIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
      const prevButton = root.querySelector('[data-pdp-media-prev]');
      const nextButton = root.querySelector('[data-pdp-media-next]');
      let thumbSwiper = null;

      if (!mainImages.length) return;

      const getActiveIndex = () => mainImages.findIndex((img) => img.classList.contains('is-active'));

      const setActiveByIndex = (targetIndex) => {
        const total = mainImages.length;
        if (!total) return;

        const normalizedIndex = ((targetIndex % total) + total) % total;
        const activeImage = mainImages[normalizedIndex];
        if (!activeImage) return;

        const mediaId = activeImage.dataset.mainMediaId;
        if (!mediaId) return;

        setActiveMedia(mediaId);
      };

      const syncValueBadge = (mediaId) => {
        if (!tagsContainer) return;

        const normalizedMediaId = String(mediaId);
        const shouldShow = variantFeaturedMediaIds.has(normalizedMediaId);

        tagsContainer.hidden = !shouldShow;
        tagsContainer.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      };

      const setActiveMedia = (mediaId) => {
        mainImages.forEach((img) => {
          const isActive = img.dataset.mainMediaId === mediaId;
          img.classList.toggle('is-active', isActive);
          img.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        thumbs.forEach((thumb) => {
          const isActive = thumb.dataset.targetMediaId === mediaId;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (thumbSwiper) {
          const activeThumbIndex = thumbs.findIndex((thumb) => thumb.dataset.targetMediaId === mediaId);
          if (activeThumbIndex >= 0) {
            thumbSwiper.slideTo(activeThumbIndex);
          }
        }

        syncValueBadge(mediaId);
      };

      // Public API: allow external code (e.g. variant selection) to switch the active media
      root.setActiveMedia = (mediaId) => {
        if (!mediaId) return;
        setActiveMedia(String(mediaId));
      };

      const handleThumbActivate = (event) => {
        const target = event.target instanceof Element ? event.target.closest('[data-target-media-id]') : null;
        if (!target || !root.contains(target)) return;

        const mediaId = target.dataset.targetMediaId;
        if (!mediaId) return;

        event.preventDefault();
        setActiveMedia(mediaId);
      };

      root.addEventListener('click', handleThumbActivate);

      const handlePrevClick = (event) => {
        event.preventDefault();
        const currentIndex = getActiveIndex();
        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        setActiveByIndex(safeIndex - 1);
      };

      const handleNextClick = (event) => {
        event.preventDefault();
        const currentIndex = getActiveIndex();
        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        setActiveByIndex(safeIndex + 1);
      };

      if (prevButton) {
        prevButton.addEventListener('click', handlePrevClick);
      }

      if (nextButton) {
        nextButton.addEventListener('click', handleNextClick);
      }

      const thumbKeydownHandlers = [];

      thumbs.forEach((thumb) => {
        const handleThumbKeydown = (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            const mediaId = thumb.dataset.targetMediaId;
            if (!mediaId) return;
            event.preventDefault();
            setActiveMedia(mediaId);
          }
        };

        thumb.addEventListener('keydown', handleThumbKeydown);
        thumbKeydownHandlers.push({ thumb, handleThumbKeydown });
      });

      let handleTouchStart = null;
      let handleTouchEnd = null;

      if (mainContainer && mainImages.length > 1) {
        let touchStartX = 0;
        let touchStartY = 0;

        handleTouchStart = (event) => {
          const touch = event.changedTouches && event.changedTouches[0];
          if (!touch) return;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
        };

        handleTouchEnd = (event) => {
          const touch = event.changedTouches && event.changedTouches[0];
          if (!touch) return;

          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;

          // Ignore vertical gestures and tiny swipes.
          if (Math.abs(deltaX) < 24 || Math.abs(deltaY) > Math.abs(deltaX)) return;

          const currentIndex = getActiveIndex();
          const safeIndex = currentIndex >= 0 ? currentIndex : 0;

          if (deltaX < 0) {
            setActiveByIndex(safeIndex + 1);
          } else {
            setActiveByIndex(safeIndex - 1);
          }
        };

        mainContainer.addEventListener('touchstart', handleTouchStart, { passive: true });

        mainContainer.addEventListener('touchend', handleTouchEnd, { passive: true });
      }

      registerMediaCleanup(root, () => {
        root.removeEventListener('click', handleThumbActivate);
        if (prevButton) {
          prevButton.removeEventListener('click', handlePrevClick);
        }
        if (nextButton) {
          nextButton.removeEventListener('click', handleNextClick);
        }

        thumbKeydownHandlers.forEach(({ thumb, handleThumbKeydown }) => {
          thumb.removeEventListener('keydown', handleThumbKeydown);
        });

        if (mainContainer && handleTouchStart && handleTouchEnd) {
          mainContainer.removeEventListener('touchstart', handleTouchStart);
          mainContainer.removeEventListener('touchend', handleTouchEnd);
        }

        if (thumbSwiper) {
          thumbSwiper.destroy(true, true);
          thumbSwiper = null;
        }

        delete root.setActiveMedia;
      });

      if (thumbSwiperEl && thumbs.length > 1 && typeof Swiper !== 'undefined') {
        thumbSwiper = new Swiper(thumbSwiperEl, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true,
          preventClicks: false,
          preventClicksPropagation: false,
        });
      }

      const currentActiveMedia = mainImages.find((img) => img.classList.contains('is-active'));
      if (currentActiveMedia && currentActiveMedia.dataset.mainMediaId) {
        setActiveMedia(currentActiveMedia.dataset.mainMediaId);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomPdpMedia(document));
  } else {
    initCustomPdpMedia(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target) {
      initCustomPdpMedia(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target) return;

    forEachMediaRootInScope(event.target, (root) => {
      runMediaCleanup(root);
      root.dataset.mediaInitialized = 'false';
    });
  });
})();
